import { useState } from 'react';
import {
  DIAS_VENCIMENTO_SALDO,
  DIAS_VENCIMENTO_SINAL,
  DOCUMENTOS,
  MAX_PARCELAS_SALDO,
  formatarBRL,
  valoresVigentes,
  type Candidato,
  type Cobranca,
  type MarcoId,
  type TipoCobranca,
  type TipoDocumento,
} from '@aponte/shared';
import type { DocumentoNaFicha } from '../../api/cliente.js';
import { useSessao } from '../../autenticacao/ContextoDeSessao.js';

interface Props {
  candidato: Candidato;
  cobrancas: Cobranca[];
  documentos: DocumentoNaFicha[];
  ocupado: boolean;
  aoEmitir: (tipo: TipoDocumento) => void;
  aoRegistrarAssinatura: (documento: DocumentoNaFicha) => void;
  aoCobrar: (tipo: TipoCobranca, parcelas: number) => void;
  aoDecidirCuradoria: (aprovado: boolean) => void;
  aoMarcarAceite: () => void;
  aoRegistrarOnboarding: () => void;
}

/**
 * A esteira de A Ponte, na ordem em que a operação executa.
 *
 * Cada passo só libera quando o anterior tem seu marco carimbado — a mesma regra que a
 * API aplica do lado do servidor. Aqui a trava é conforto do operador; lá é garantia.
 *
 * Emissão e assinatura são passos **separados** de propósito: gerar o PDF não prova que
 * o Proponente concordou com nada, e é o documento assinado que move o contrato adiante.
 */
export function Esteira({
  candidato,
  cobrancas,
  documentos,
  ocupado,
  aoEmitir,
  aoRegistrarAssinatura,
  aoCobrar,
  aoDecidirCuradoria,
  aoMarcarAceite,
  aoRegistrarOnboarding,
}: Props) {
  const { temPapel } = useSessao();
  const { sinal, saldo } = valoresVigentes(candidato);
  const marcos = candidato.marcos;
  const [parcelasDoSaldo, setParcelasDoSaldo] = useState(1);

  const cobrancaDe = (tipo: TipoCobranca) =>
    cobrancas.filter((c) => c.tipo === tipo).at(-1) ?? null;

  /** Versão vigente de cada tipo — a assinatura se registra sobre a mais nova. */
  const documentoDe = (tipo: TipoDocumento): DocumentoNaFicha | null =>
    documentos
      .filter((d) => d.tipo === tipo)
      .sort((a, b) => a.versao - b.versao)
      .at(-1) ?? null;

  const podeCurar = temPapel('curadoria');
  const podeOperar = temPapel('closer');

  const passos: Passo[] = [
    {
      nome: 'Carta de Intenções',
      descricao:
        `Emitida com os dados da ficha e o sinal de ${formatarBRL(sinal)} já calculado. ` +
        'A emissão congela os valores do contrato: mudança futura de tabela não afeta mais ' +
        'este candidato.',
      liberado: true,
      marco: 'cartaEmitida',
      acoes: podeOperar ? (
        <>
          <button className="bt pequeno" disabled={ocupado} onClick={() => aoEmitir('carta')}>
            {marcos.cartaEmitida ? 'Reemitir Carta' : 'Emitir Carta'}
          </button>
          <AvisoDeDivergencia documento={documentoDe('carta')} />
        </>
      ) : null,
    },
    {
      nome: 'Carta assinada de volta',
      descricao:
        'Válida até a decisão do comitê, desde que o sinal seja pago em até ' +
        `${DIAS_VENCIMENTO_SINAL} dias corridos da assinatura (cl. 7.1).`,
      liberado: !!marcos.cartaEmitida,
      marco: 'cartaAssinadaRecebida',
      acoes: podeOperar ? (
        <BotaoDeAssinatura
          documento={documentoDe('carta')}
          ocupado={ocupado}
          aoRegistrar={aoRegistrarAssinatura}
        />
      ) : null,
    },
    {
      nome: `Link do sinal · 5% · ${formatarBRL(sinal)}`,
      descricao:
        'À vista, cartão ou Pix, sem parcelamento. Split de 70% para a Academia aplicado ' +
        'pelo gateway. O pagamento confirmado pelo Asaas move a ficha para curadoria sozinho.',
      liberado: !!marcos.cartaEmitida,
      marco: 'sinalPago',
      acoes: podeOperar ? (
        <>
          <button className="bt pequeno" disabled={ocupado} onClick={() => aoCobrar('sinal', 1)}>
            Gerar link do sinal
          </button>
          <LinkDaCobranca cobranca={cobrancaDe('sinal')} />
        </>
      ) : null,
    },
    {
      nome: 'Curadoria',
      descricao:
        'Decisão de Gilson Rodrigues em até 10 dias úteis contados do sinal compensado. ' +
        'Sem aprovação, o sinal é devolvido integralmente em até 5 dias úteis.',
      liberado: !!marcos.sinalPago,
      marco: 'curadoriaDecidida',
      acoes: podeCurar ? (
        <>
          <button
            className="bt pequeno"
            disabled={ocupado}
            onClick={() => aoDecidirCuradoria(true)}
          >
            Aprovar
          </button>
          <button
            className="bt perigo pequeno"
            disabled={ocupado}
            onClick={() => aoDecidirCuradoria(false)}
          >
            Não aprovar
          </button>
        </>
      ) : (
        <span className="aguardando">Decisão exclusiva do comitê de curadoria.</span>
      ),
    },
    {
      nome: 'Termo de Aceite e Regimento',
      descricao:
        'Emitidos somente após a aprovação. O Regimento Interno é anexo indissociável do Termo.',
      liberado: ['aprovado', 'aceite', 'ativo'].includes(candidato.etapa),
      marco: 'termoEmitido',
      acoes: podeOperar ? (
        <>
          <button className="bt pequeno" disabled={ocupado} onClick={() => aoEmitir('termo')}>
            Emitir Termo
          </button>
          <button className="bt pequeno" disabled={ocupado} onClick={() => aoEmitir('regimento')}>
            Emitir Regimento
          </button>
          <AvisoDeDivergencia documento={documentoDe('termo')} />
        </>
      ) : null,
    },
    {
      nome: 'Termo assinado e aceite',
      descricao:
        'É o Termo assinado que firma o contrato — emitir o PDF não comprova aceitação. ' +
        'Só depois disso a cadeira pode ser aceita e o saldo, cobrado.',
      liberado: !!marcos.termoEmitido,
      marco: 'termoAssinadoRecebido',
      acoes: podeOperar ? (
        <>
          <BotaoDeAssinatura
            documento={documentoDe('termo')}
            ocupado={ocupado}
            aoRegistrar={aoRegistrarAssinatura}
          />
          {marcos.termoAssinadoRecebido && candidato.etapa === 'aprovado' && (
            <button className="bt pequeno" disabled={ocupado} onClick={aoMarcarAceite}>
              Marcar aceite
            </button>
          )}
        </>
      ) : null,
    },
    {
      nome: `Link do saldo · 95% · ${formatarBRL(saldo)}`,
      descricao: `Cartão à vista ou em até ${MAX_PARCELAS_SALDO}x, ou Pix. Mesmo split de 70/30. Vencimento em ${DIAS_VENCIMENTO_SALDO} dias.`,
      liberado: !!marcos.termoAssinadoRecebido,
      marco: 'saldoPago',
      acoes: podeOperar ? (
        <>
          <label htmlFor="parcelas" style={{ margin: 0 }}>
            Parcelas
          </label>
          <select
            id="parcelas"
            value={parcelasDoSaldo}
            onChange={(e) => setParcelasDoSaldo(Number(e.target.value))}
            style={{ width: 'auto', padding: '8px 10px', fontSize: 12 }}
          >
            {Array.from({ length: MAX_PARCELAS_SALDO }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {n}x de {formatarBRL(Math.round(saldo / n))}
              </option>
            ))}
          </select>
          <button
            className="bt pequeno"
            disabled={ocupado}
            onClick={() => aoCobrar('saldo', parcelasDoSaldo)}
          >
            Gerar link do saldo
          </button>
          <LinkDaCobranca cobranca={cobrancaDe('saldo')} />
        </>
      ) : null,
    },
    {
      nome: 'Onboarding',
      descricao:
        'Entrega do selo numerado e do cartão em cerimônia com o comitê e presença do fundador.',
      liberado: !!marcos.saldoPago,
      marco: 'onboardingRealizado',
      acoes: podeOperar ? (
        <button className="bt vazado pequeno" disabled={ocupado} onClick={aoRegistrarOnboarding}>
          Registrar onboarding
        </button>
      ) : null,
    },
  ];

  return (
    <div>
      {passos.map((passo, i) => (
        <div key={passo.nome} className={`passo ${passo.liberado ? '' : 'bloqueado'}`}>
          <div className="numero">{String(i + 1).padStart(2, '0')}</div>
          <div className="corpo">
            <div className="nome">{passo.nome}</div>
            <div className="descricao">{passo.descricao}</div>
            <div className="acoes">
              {passo.liberado ? (
                <>
                  {passo.acoes}
                  {marcos[passo.marco] && (
                    <span className="feito">
                      ✓ {new Date(marcos[passo.marco]!).toLocaleDateString('pt-BR')}
                    </span>
                  )}
                </>
              ) : (
                <span className="aguardando">Aguarda a etapa anterior</span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

interface Passo {
  nome: string;
  descricao: string;
  liberado: boolean;
  marco: MarcoId;
  acoes: React.ReactNode;
}

/** Aviso de que o PDF emitido já não bate com a ficha. */
function AvisoDeDivergencia({ documento }: { documento: DocumentoNaFicha | null }) {
  if (!documento?.desatualizado) return null;
  return (
    <span className="aviso-desatualizado">
      ⚠ A ficha mudou depois desta emissão (v{documento.versao}) — reemita antes de assinar.
    </span>
  );
}

function BotaoDeAssinatura({
  documento,
  ocupado,
  aoRegistrar,
}: {
  documento: DocumentoNaFicha | null;
  ocupado: boolean;
  aoRegistrar: (documento: DocumentoNaFicha) => void;
}) {
  if (!documento) {
    return <span className="aguardando">Emita o documento primeiro.</span>;
  }

  if (documento.assinadoRecebidoEm) {
    return (
      <span className="arquivo-assinado">
        {DOCUMENTOS[documento.tipo].nome} v{documento.versao} assinado
        {documento.chaveS3Assinado ? ', com arquivo anexado' : ', sem arquivo anexado'}.
      </span>
    );
  }

  return (
    <button className="bt pequeno" disabled={ocupado} onClick={() => aoRegistrar(documento)}>
      Registrar assinatura
    </button>
  );
}

function LinkDaCobranca({ cobranca }: { cobranca: Cobranca | null }) {
  const [copiado, setCopiado] = useState(false);
  if (!cobranca) return null;

  return (
    <>
      <a
        href={cobranca.urlFatura}
        target="_blank"
        rel="noopener noreferrer"
        style={{ fontSize: 11 }}
      >
        Abrir fatura ({cobranca.situacao.toLowerCase()})
      </a>
      <button
        className="copiar"
        style={{ marginTop: 0 }}
        onClick={() => {
          void navigator.clipboard.writeText(cobranca.urlFatura).then(() => {
            setCopiado(true);
            setTimeout(() => setCopiado(false), 2000);
          });
        }}
      >
        {copiado ? 'Copiado' : 'Copiar link'}
      </button>
    </>
  );
}
